import { DatePipe } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
// TODO: these services are imported individually until SignInservice is refactored to avoid circular dependency
import { AppStateService } from '@services/app-state.service';
import { IUserProfile, IUserProfileInfo } from '@services/profile.interface';
import { PageMonitor } from '@services/page-monitor';
import { ProfileService } from '@services/profile.service';
import { SegmentService } from '@services/segment.service';
import {
  CartDetails,
  EmailDocsRequestBody,
  FileExtension,
  FundComentaryContent,
  FundCommentaryDocument,
  LiteratureContentDocument,
  LiteratureData,
  LiteratureDocument,
  LiteratureDocumentData,
  LiteratureDocumentListingData,
  LiteratureHistoryData,
  LiteratureRequestBodyProfile,
  MailFormValues,
  MailRequestBody,
  MailResponseError,
  MailResponseObject,
  SegmentId,
} from '@types';
import { FA, FP } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { asyncScheduler, Observable, Subject } from 'rxjs';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LiteratureApiService } from './api-services/literature-api.service';
import { LiteratureGlobalConfigService } from './literature-global-config.service';

const logger = Logger.getLogger('LiteratureDataService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureDataService implements OnDestroy {
  private emailBaseUrl: string;
  profileData: IUserProfile;
  private cachedObservables: Map<
    string,
    Observable<LiteratureData>
  > = new Map();
  private cachedFundComentaryContentObservables: Map<
    string,
    Observable<FundComentaryContent>
  > = new Map();
  mailDetails: MailFormValues;
  mailResponseDetails: MailResponseObject;
  mailResponseError: MailResponseError;
  isInternational: boolean;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private appStateService: AppStateService,
    private segmentService: SegmentService,
    private profileService: ProfileService,
    private literatureConfig: LiteratureGlobalConfigService,
    private literatureApiService: LiteratureApiService,
    public dateFormatter: DatePipe
  ) {}

  public getProfileData(): IUserProfile {
    return this.profileData;
  }

  public setProfileData(profileData: IUserProfile) {
    this.profileData = profileData;
  }

  private getApiData(
    useCache: boolean,
    params: Map<string, string>
  ): Observable<LiteratureData> {
    this.isInternational = this.literatureConfig.isInternational;
    const url = this.getUrl(params);
    logger.debug('literature document data url: ', url);
    if (useCache && this.cachedObservables.has(url)) {
      return this.cachedObservables.get(url);
    }
    const req = PageMonitor.startAjaxRequest(url);
    const liData$ = this.appStateService.getuseAPIM()
      ? this.literatureApiService
          .getLiteratureDataAPIMFlow(url, this.getHeaders())
          .pipe(
            tap(() => PageMonitor.endAjaxRequest(req)),
            shareReplay(1)
          )
      : this.literatureApiService
          .getLiteratureData(url, this.getHeaders())
          .pipe(
            tap(() => PageMonitor.endAjaxRequest(req)),
            shareReplay(1)
          );
    if (useCache) {
      this.cachedObservables.set(url, liData$);
      asyncScheduler.schedule(() => {
        this.cachedObservables.delete(url);
      }, 10000);
    }
    return liData$;
  }
  getHeaders() {
    return {
      dealerNo: this.profileData?.profileInfo?.dealerNo
        ? this.profileData?.profileInfo?.dealerNo
        : '',
    };
  }
  // ToDo: fix return type
  private postEmailDocumentsAndObserveEvents(
    url: string,
    requestBody: EmailDocsRequestBody
  ): Observable<any> {
    return this.literatureApiService.postEmailDocumentsAndObserveEvents(
      url,
      requestBody,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }
  // ToDo: fix return type
  private postEmailDocuments(
    url: string,
    requestBody: EmailDocsRequestBody
  ): Observable<any> {
    if (this.appStateService.getuseAPIM()) {
      const emailDocsAPIMUrl = `${this.appStateService.getLiteratureEmailDocsAPIMUrl()}?channel=${this.appStateService.getChannel()}`;
      return this.literatureApiService.postEmailDocumentsAPIMFlow(
        emailDocsAPIMUrl,
        requestBody
      );
    } else {
      return this.literatureApiService.postEmailDocuments(url, requestBody);
    }
  }
  // ToDo: fix return type
  public emailDocuments(
    recipients: string[],
    litCodes: string[]
  ): Observable<any> {
    this.emailBaseUrl = `${this.appStateService.getLiteratureBaseApiUrl()}/email?channel=${this.appStateService.getChannel()}`;

    const requestBody: EmailDocsRequestBody = {
      emailIds: recipients,
      literatureCodes: litCodes,
    };
    return this.postEmailDocuments(this.emailBaseUrl, requestBody);
  }

  public mailDocument(
    mailRequestBody: MailRequestBody,
    checkoutMode?: string
  ): Observable<MailResponseObject> {
    mailRequestBody.emailOrderStatus = true;

    if (this.appStateService.getuseAPIM()) {
      let url = '';
      if ('download' === checkoutMode) {
        url = `${this.appStateService.getLiteratureBaseApiUrl()}/order?checkoutMode=download`;
      } else {
        url = this.appStateService.getSubmitOrderAPIMUrl();
      }

      return this.literatureApiService.mailDocumentAPIMFlow(
        url,
        mailRequestBody
      );
    } else {
      let url = '';
      if ('download' === checkoutMode) {
        url = `${this.appStateService.getLiteratureBaseApiUrl()}/order?checkoutMode=download`;
      } else {
        url = `${this.appStateService.getLiteratureBaseApiUrl()}/order`;
      }

      return this.literatureApiService.mailDocument(url, mailRequestBody);
    }
  }

  setPostMailResponse(responseData: MailResponseObject): void {
    this.mailResponseDetails = responseData;
  }

  setPostMailError(responseData: MailResponseError): void {
    this.mailResponseError = responseData;
  }

  getPostMailError(): MailResponseError {
    return this.mailResponseError;
  }
  getPostMailResponse(): MailResponseObject {
    return this.mailResponseDetails;
  }

  getmailDetails(): MailFormValues {
    return this.mailDetails;
  }

  setMailDetails(formData: MailFormValues): void {
    this.mailDetails = formData;
  }

  getMailFormValue(): MailFormValues {
    return this.mailDetails;
  }

  public getContentLiteratureDocumentsForDisplay(
    literatureDocument: LiteratureDocument[],
    literatureContentDocument: LiteratureContentDocument[]
  ): LiteratureDocument[] {
    const literatureDocumentMap: Map<string, LiteratureDocument> = new Map();
    const literatureContentDocumentsMap: Map<
      string,
      LiteratureContentDocument
    > = new Map();
    const mappedLiteratureDocument = [];
    if (literatureContentDocument) {
      literatureContentDocument.forEach((item) => {
        literatureContentDocumentsMap.set(item.literatureCode, item);
      });
      literatureDocument.forEach((docItem) => {
        if (literatureContentDocumentsMap.has(docItem.docId)) {
          const litContentDoc = literatureContentDocumentsMap.get(
            docItem.docId
          );
          if (!!litContentDoc.title) {
            docItem.dctermsTitle = litContentDoc.title;
          }
          if (!!litContentDoc.description) {
            docItem.dctermsDescription = litContentDoc.description;
          }
          if (!!litContentDoc.url) {
            docItem.href = litContentDoc.url;
          } else {
            docItem.href = `${this.literatureConfig?.navigationUrls?.litDetailUrl}/${docItem.docId}`;
          }
        }
        literatureDocumentMap.set(docItem.docId, docItem);
      });
      literatureContentDocument.forEach((item) => {
        if (literatureDocumentMap.has(item.literatureCode)) {
          mappedLiteratureDocument.push(
            literatureDocumentMap.get(item.literatureCode)
          );
        }
      });
    }
    return mappedLiteratureDocument;
  }
  public literatureUserProfileMapper(
    profileInfo: IUserProfileInfo
  ): LiteratureRequestBodyProfile {
    return {
      userId: profileInfo.userId,
      role: profileInfo.role,
      webExperience: profileInfo.webExperience,
      displayName: profileInfo.displayName,
      firm: profileInfo.firm,
      addressLine1: profileInfo.addressLine1,
      city: profileInfo.city,
      state: profileInfo.state,
      zip: profileInfo.zip,
      phoneNumber: profileInfo.phoneNumber,
      email: profileInfo.email,
      businessKey: profileInfo.businessKey,
      userSysNo: profileInfo.userSysNo,
      loginName: profileInfo.loginName,
      dealerNumber: profileInfo.dealerNo,
    };
  }

  public literatureMapper(data: LiteratureDocumentData): LiteratureDocument {
    return {
      frkOneTISNumber: data.frkOneTISNumber
        ? data.frkOneTISNumber.split(',')
        : [],
      docId: this.getDocId(data),
      href: data.href,
      dctermsTitle: data.dctermsTitle,
      dctermsDescription: data.dctermsDescription,
      dctermsAudience: data.dctermsAudience,
      dctermsType: data.dctermsType,
      publicationDate: data.publicationDate,
      frkShareClassCode: data.frkShareClassCode
        ? data.frkShareClassCode
            .split(',')
            .map((shareClass) => shareClass?.trim())
        : [],
      literatureHref: data.literatureHref,
      frkReferenceDate: data.frkReferenceDate
        ? data.frkReferenceDate
        : data.publicationDate,
      frkReferenceYear: data.frkReferenceDate
        ? this.getYear(data.frkReferenceDate)
        : this.getYear(data.publicationDate),
      fileExtension: data.fileExtension
        ? FileExtension[data.fileExtension.split(' ')[0].toLocaleLowerCase()]
        : this.assignExtension(data),
      frkFundGroupCode: data.frkFundGroupCode,
      cmsExternalLink: data.cmsExternalLink,
      nextUpdateDate: data.nextUpdateDate,
      clientUse:
        data.isClientUse !== undefined
          ? data.isClientUse
            ? 'Yes'
            : 'No'
          : data.isClientUse,
      topic: data.topic ? data.topic.split(',') : [],
      subTopic: data.subTopic ? data.subTopic : '',
      downloadUrl: data.downloadUrl,
      fundName: data.fundName ? data.fundName : '',
      availableInCart: false,
      thumbnailPath: data.thumbnailPath,
      imageUrl: null,
      finraLink: null,
      assetCategory: data.assetCategory ? data.assetCategory : '',
      orderLimit: data.orderLimit,
      isOrdItself: data.isOrdItself,
      isInvestor: data.isInvestor,
      exemptCustomers: data.exemptCustomers?.split(','),
      mandatoryMaterial: data.mandatoryMaterial,
      fileSize: data.fileSize,
      description: data.description,
      complementaryMaterial: data.complementaryMaterial,
      isInv: data.isInv,
      keywords: data.keywords,
      blueSkyStates: data.blueSkyStates,
      q1ph: data.q1ph,
      q3ph: data.q3ph,
      restrictedDealer: data.restrictedDealer,
      isWidenData: data.thumbnailPath?.indexOf('https') > -1,
      litPathPrevious: data.litPathPrevious,
      customFilePath: data.customFilePath,
      orderDate: data.orderDate,
      dctermsLanguage: data.dctermsLanguage
        ? data.dctermsLanguage.split(',')
        : [],
      moreLink: data.moreLink,
      isDealerUse: data?.isDealerUse === 'Y' ? true : false,
      showShareClass: false,
      languageFilterValues: [],
      pdfNumberingAppID: data.pdfNumberingAppID,
    };
  }

  // NGC-13511 - fileExtension property missing from backend for institutional investor's documents
  // As fileExtension aren't available for INTL site documents, icon mismatch for excel files occured with
  // pdfs. To solve this adding conditions so that it scans download Url from
  // the document and send the available fileExtension. If there are no downloadUrl available then by
  // default PDF icon will be sent.
  /**
   *
   * @param data Literature document data whose fileExtension is missing
   * @returns Appropriate file Extension of data
   */
  public assignExtension(data: LiteratureDocumentData): string {
    const downloadUrl = data.downloadUrl;
    if (downloadUrl) {
      if (downloadUrl.includes('.pdf')) {
        return FileExtension.pdf;
      } else if (
        downloadUrl.includes('.doc') ||
        downloadUrl.includes('.docx')
      ) {
        return FileExtension.doc;
      } else if (downloadUrl.includes('.pptx')) {
        return FileExtension.pptx;
      } else if (downloadUrl.includes('.oft')) {
        return FileExtension.oft;
      } else if (
        downloadUrl.includes('.xls') ||
        downloadUrl.includes('.xlsx') ||
        downloadUrl.includes('.xlsm')
      ) {
        return FileExtension.xls;
      } else {
        return FileExtension.pdf;
      }
    }
    return FileExtension.pdf;
  }

  public getYear(date: string): string {
    if (date) {
      return this.dateFormatter.transform(date, 'MM/dd/yyyy').split('/')[2];
    }
    return '';
  }

  getCommentaryDownloadDocUrl(doc: FundCommentaryDocument): string {
    let url = '';
    if (this.isInternational) {
      url = `${this.getDownloadUrl('listing')}${doc.customFilePath}`;
    } else {
      url = `${this.getDownloadUrl('literatureUS')}${doc.customFilePath}`;
    }
    return url;
  }

  getDocId(data: LiteratureDocumentData): string {
    if (this.isInternational) {
      return data.livesiteId ? data.livesiteId : data.literatureCode;
    }
    return data.customFilePath;
  }

  getCustomDownloadDocUrl(link: string, docType: string): string {
    let url = '';
    let docName = '';
    let docId: string[];
    if (link.includes('/')) {
      docName = link?.substring(link?.lastIndexOf('/') + 1);
      docId = docName.split('_');
      url = `${this.getDownloadUrl('literatureUS')}${docId[0]}-${docType}`;
    } else {
      docName = link;
      docId = [docName];
      url = `${this.getDownloadUrl('literatureUS')}${docId[0]}`;
    }
    //  url = `${this.getDownloadUrl('literatureUS')}${docId[0]}-${docType}`;
    return url;
  }

  mapLiteratureUrl(data: LiteratureDocument) {
    if (this.isInternational) {
      if (data.pdfNumberingAppID) {
        data.pdfDownloadLink = `${this.getDownloadUrl('pdfnumbering')}${
          data.literatureHref
        }`;
      } else {
        data.pdfDownloadLink = data.cmsExternalLink
          ? data.cmsExternalLink
          : `${this.getDownloadUrl('listing')}${data.literatureHref}`;
      }
    } else {
      if (data.thumbnailPath) {
        if (data.isWidenData || data.thumbnailPath?.indexOf('https') > -1) {
          data.imageUrl = `${data.thumbnailPath}&w=500&quality=90`;
        } else {
          data.imageUrl = `${this.getDownloadUrl('image')}${
            data.thumbnailPath
          }`;
        }
      }
      data.finraLink = `${this.getDownloadUrl('finra')}${data.docId}`;
      // add for widen non client use data
      data.pdfDownloadLink = `${this.getDownloadUrl('literatureUS')}${
        data.customFilePath
      }`;
    }
  }

  mapCustomLiteratureUrl(data, property): boolean {
    if (data[property]) {
      data.pdfDownloadLink = this.getCustomDownloadDocUrl(
        data[property],
        property.toUpperCase()
      );
      return true;
    } else {
      delete data.pdfDownloadLink;
      return false;
    }
  }

  isSiteIntl(): boolean {
    return this.isInternational;
  }
  public loadData(
    params: Map<string, string>,
    useCache = false,
    useProfile = false
  ): Observable<LiteratureData> {
    if (useProfile) {
      return this.getProfileData$()?.pipe(
        switchMap((profileData) => {
          this.setProfileData(profileData);
          return this.getApiData(useCache, cloneDeep(params));
        })
      );
    } else {
      return this.getApiData(useCache, cloneDeep(params));
    }
  }

  public getProfileData$(): Observable<IUserProfile> {
    return this.profileService.getUserProfile$()?.pipe(
      filter((profile: IUserProfile): boolean => {
        if (profile.isLoggedIn) {
          return !!profile?.profileInfo?.userId;
        } else {
          return true;
        }
      })
    );
  }

  isLiteratureAddOnAccount(
    literatureCode: string,
    literatureContentDocuments: LiteratureContentDocument[]
  ): LiteratureContentDocument {
    // this.literatureContentDocuments = literatureContentDocuments;
    return literatureContentDocuments?.find(
      (item) => item.literatureCode === literatureCode
    );
  }
  private getUrl(params: Map<string, string>): string {
    // const url = this.appStateService.getLiteratureApiUrl();
    let url = this.appStateService.getLiteratureApiUrl();

    if (this.appStateService.getuseAPIM()) {
      url = this.appStateService.getLiteratureBaseAPIMUrl();
    } else {
      url = this.appStateService.getLiteratureApiUrl();
    }
    // TODO:- remove when proper data sync is done at widen side for south korea
    const channel =
      this.appStateService.getChannel() === 'en-ko'
        ? 'en-lu'
        : this.appStateService.getChannel();
    return url + '?channel=' + channel + this.getParams(params);
  }
  private getParams(paramsMap: Map<string, string>): string {
    if (!this.isInternational) {
      this.addActorParams(paramsMap);
    }
    let paramUrl = '';
    if (paramsMap) {
      paramsMap.delete('componentName');
      for (const entry of paramsMap.entries()) {
        paramUrl = paramUrl + '&' + entry[0] + '=' + entry[1];
      }
    }
    return paramUrl;
  }
  private addActorParams(paramsMap: Map<string, string>) {
    const segmentId = this.segmentService.getCurrentSegmentId();
    if (SegmentId.INVESTOR === segmentId) {
      paramsMap.set('loggedIn', 'n');
    } else if (SegmentId.FINANCIAL_PROFESSIONALS === segmentId) {
      if (this.profileData?.isLoggedIn) {
        paramsMap.set('loggedIn', 'y');
      } else {
        paramsMap.set('loggedIn', 'n');
      }
    }
    // Temp fix: this is temporary fix for lagacy platform
    if (this.appStateService.getChannel() === 'us-institutional') {
      paramsMap.set('audience', SegmentId.FINANCIAL_PROFESSIONALS);
    } else {
      paramsMap.set('audience', segmentId);
    }
  }
  getDownloadUrl(type: string): string {
    if ('finra' === type) {
      return this.appStateService.getLiteratureDownloadFinraUrl();
    } else if ('literatureUS' === type) {
      return this.appStateService.getLiteratureDownloadUrl();
    } else if ('base' === type) {
      return this.appStateService.getLiteratureBaseApiUrl();
    } else if ('listing' === type) {
      return this.appStateService.getLiteratureDownloadBaseUrl();
    } else if ('image' === type) {
      return this.appStateService.getLiteratureImageUrl();
    } else if ('pdfnumbering' === type) {
      return this.appStateService.getLiteratureDownloadAutoNumberingUrl();
    }
  }

  /**
   * Get literature history
   * @param profileInfo profile data
   * @returns  Literature history
   */
  public getLiteratureHistory(
    profileInfo: IUserProfileInfo
  ): Observable<LiteratureHistoryData> {
    const url = `${this.appStateService.getLiteratureBaseApiUrl()}/literatureHistory`;
    const profile = this.literatureUserProfileMapper(profileInfo);

    return this.literatureApiService.getLiteratureHistory(url, profile);
  }

  /**
   * map LiteratureDocumentData to LiteratureDocumentListingData
   * @param litHistDetail LiteratureDocumentData[]
   * @returns LiteratureDocumentListingData[]
   */
  public mapToLiteratureDocument(
    litHistDetail: LiteratureDocumentData[]
  ): LiteratureDocumentListingData[] {
    const returnData: LiteratureDocumentListingData[] = [];
    for (const litData of litHistDetail) {
      const literatureData = this.literatureMapper(litData);
      this.mapLiteratureUrl(literatureData);
      returnData.push({ literatureData });
    }
    return returnData;
  }

  /**
   * Delete Literature Document
   * @param profileInfo: IUserProfileInfo
   * @returns response
   */
  public deleteLiteratureDocument(
    profileInfo: IUserProfileInfo,
    litCode: string
  ): Observable<any> {
    const url = `${this.appStateService.getLiteratureBaseApiUrl()}/removeLiterature`;
    const profile = this.literatureUserProfileMapper(profileInfo);
    const remLitDetail = [
      {
        litCode,
        count: 0,
      },
    ];

    const requestHeaders = { ...profile, remLitDetail };
    return this.literatureApiService.deleteLiteratureDocument(
      url,
      requestHeaders
    );
  }
  prepareDataToPlaceOrder(data: CartDetails[]) {
    if (!this.canOrderPlaced()) {
      return;
    }
    const fullName = this.profileData?.profileInfo.displayName.split(' ');
    const mailBody: MailRequestBody = {
      deliverTo: 'me',
      firstName: fullName[0],
      lastName: fullName[fullName.length - 1],
      zip: this.profileData?.profileInfo.zip,
      city: this.profileData?.profileInfo.city,
      addressLine1: this.profileData?.profileInfo.addressLine1,
      addressLine2: '',
      emailAddress: this.profileData?.profileInfo.email,
      workPhone: this.profileData?.profileInfo.phoneNumber,
      stateCode: this.profileData?.profileInfo.state,
      cartItems: data,
      userInfo: this.literatureUserProfileMapper(this.profileData.profileInfo),
      mailStateCode: this.profileData?.profileInfo.state,
    };
    this.mailDocument(mailBody, 'download').subscribe(
      (response) => logger.info(response),
      () => logger.info('unable to place order')
    );
  }

  /**
   * check order can be placed only when user is Logged-In and role is FA or FP in case of document downloaded or email is sent
   * @returns boolean flag based logged-In user role
   */
  canOrderPlaced(): boolean {
    return (
      this.profileData?.isLoggedIn &&
      (this.profileData?.profileInfo?.role === FA ||
        this.profileData?.profileInfo?.role === FP)
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  getFundCommentaryContent(
    paramMap: any,
    useCache = false,
    isContentFromTS: boolean
  ): Observable<FundComentaryContent> {
    const url = isContentFromTS ? this.getContentUrl(paramMap) : paramMap;
    if (useCache && this.cachedFundComentaryContentObservables.has(url)) {
      return this.cachedFundComentaryContentObservables.get(url);
    }
    const headers = new HttpHeaders({
      'X-FT-API-KEY': this.appStateService.getFundCommentaryKey(),
      accept: 'application/*',
    });
    const req = PageMonitor.startAjaxRequest(url);
    const FundCommentaryContent$ = this.literatureApiService
      .getFundCommentaryContent(url, headers)
      .pipe(
        tap(() => PageMonitor.endAjaxRequest(req)),
        shareReplay(1)
      );
    if (useCache) {
      this.cachedFundComentaryContentObservables.set(
        url,
        FundCommentaryContent$
      );
      asyncScheduler.schedule(() => {
        this.cachedFundComentaryContentObservables.delete(url);
      }, 10000);
    }
    return FundCommentaryContent$;
  }

  getContentUrl(paramMap: Map<string, string>): string {
    return (
      this.appStateService.getCommentaryContentUrl() +
      '?' +
      this.getParams(cloneDeep(paramMap))
    );
  }
}
